import React, { useEffect, useState, useRef } from 'react';
import CourseContent from './courseContent';
import Video from './video';
import Reading from './reading';
import Quiz from './quizBk';
import Sidebar from '../../layouts/sidebar';
import './microModuleDashboard.css';
import { useParams, useNavigate, useLocation} from 'react-router-dom';
import apiService from '../../../services/apiService';
import CustomToast from '../../Toast';
import Loader from '../../layouts/loader';
import CourseCompletedModal from '../modal/courseCompletedModal';
import Carousel from './carousel';
import Document from './document';

const MicroModuleDashboard = ({moduleId}) => {
  const { id: courseMetaId } = useParams();
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(modules[0]?.module_id || null);
  const [selectedModule, setSelectedModule] = useState({});
  const [lastModuleCompleted, setLastModuleCompleted] = useState(false);
  // const [selectedModuleContent, setSelectedModuleContent] = useState({});
  const [loader, setLoader] = useState(false);
  const [userData, setUserData] = useState({});
  const [selectedAns, setSelectedAns] = useState({});
  const location = useLocation();
  const course = location.state?.course || {};

  console.log("Course data from location.state:", course);

  // Initialize userDetails first before using it - get from sessionStorage for consistency
  const userDetails = JSON.parse(sessionStorage.getItem('userDetail') || '{}');
  let selectedLanguage = localStorage.getItem("selectedLanguage");
  let orgInfo = JSON.parse(localStorage.getItem("orgInfo"));
  let microCourseId = localStorage.getItem("microCourseId");
  
  // User-specific prefix for localStorage keys
  const userPrefix = userDetails?.uid ? `user_${userDetails.uid}_` : '';
  
  // Use localStorage to persist completed modules across page refreshes with user-specific keys
  const completedModulesKey = `completedModules`;
  const lastCompletedModuleKey = `lastCompletedModuleId`;
  
  // Initialize completion state from API, not localStorage
  const [completedModules, setCompletedModules] = useState([]);
  
  const [openIds, setOpenIds] = useState([]);
  const storedLastCompletedModule = localStorage.getItem(lastCompletedModuleKey);
  const [lastCompletedModuleId, setLastCompletedModuleId] = useState(storedLastCompletedModule || null);
  
  // Get course name from localStorage or state (course-specific key)
  const courseNameKey = `courseName_${courseMetaId}`;
  const [courseName, setCourseName] = useState(localStorage.getItem(courseNameKey) || '');


    useEffect(() => {
        fetchData();
    }, []);
    
   const fetchData = async () => {
        try {
            const token = apiService.getAuthToken()
            const result = await apiService.get('get-org-user-info', token);
            if (result && result?.status == "success") {
                setUserData(result?.data);
            }
        } catch (error) {
            ///setError('Error fetching user data');
            console.error(error);
        }
    };
  

  // Helper function to find a module by ID in nested structure (search sub-modules only)
  const findModuleById = (modules, targetId) => {
    if (!modules || !Array.isArray(modules) || !targetId) return null;
    
    // Log for debugging if we're looking for a problematic ID
    if (targetId === "686bac5f60cac39e9f7cf242") {
      console.log("Searching for known problematic ID:", targetId);
      console.log("Available modules at this level:", modules.map(m => ({
        id: m.module_id,
        title: m.title,
        has_submodules: !!(m.sub_modules && m.sub_modules.length > 0)
      })));
    }
    
    // Iterate through all modules at the current level
    for (const module of modules) {
      // Check sub_modules if they exist
      if (module.sub_modules && Array.isArray(module.sub_modules)) {
        for (const subModule of module.sub_modules) {
          // Check if current sub-module matches target ID
          if (subModule.module_id && subModule.module_id.toString() === targetId.toString()) {
            return subModule;
          }
          
          // Check sub_sub_modules if they exist
          if (subModule.sub_sub_modules && Array.isArray(subModule.sub_sub_modules)) {
            for (const subSubModule of subModule.sub_sub_modules) {
              if (subSubModule.module_id && subSubModule.module_id.toString() === targetId.toString()) {
                return subSubModule;
              }
            }
          }
        }
      }
    }
    
    // Not found in this branch
    return null;
  };
  
  // Helper function to flatten nested modules into a single array (sub-modules only)
  const flattenModules = (modules) => {
    let result = [];
    
    if (!modules || !Array.isArray(modules)) return result;
    
    for (const module of modules) {
      // Only include sub_modules, not the main module
      if (module.sub_modules && Array.isArray(module.sub_modules)) {
        result = result.concat(module.sub_modules);
        
        // Also check for sub_sub_modules
        module.sub_modules.forEach(subModule => {
          if (subModule.sub_sub_modules && Array.isArray(subModule.sub_sub_modules)) {
            result = result.concat(subModule.sub_sub_modules);
          }
        });
      }
    }
    
    return result;
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    // Load modules on page load
    // This will also handle loading the first module content
    fetchModules();

    // Cleanup function to ensure we're not mixing data between courses when component unmounts
    return () => {
      // Don't clear the data to ensure persistence, but let's log a message
      console.log('MicroModuleDashboard component unmounting, data preserved in localStorage');
    };
  }, []);
  
  // Listen for courseMetaId changes to load the correct completion data
  useEffect(() => {
    if (courseMetaId) {
      console.log(`Loading course: ${courseMetaId}`);
      // Load the course name for this specific course from localStorage
      const courseSpecificName = localStorage.getItem(`courseName_${courseMetaId}`);
      if (courseSpecificName && courseSpecificName !== courseName) {
        setCourseName(courseSpecificName);
      }
      // Note: Completion data will be loaded from API in fetchModules, not localStorage
    }
  }, [courseMetaId]);

  // Ensure we have a selected module when modules are loaded
  useEffect(() => {
    // Only proceed if modules are loaded but no module is selected
    if (modules.length > 0 && !selectedModuleId) {
      console.log('Modules loaded but no selectedModuleId, selecting appropriate module');
      
      let moduleToLoad = null;
      
      // First check if there's a last completed module
      if (lastCompletedModuleId) {
        const flatModules = flattenModules(modules);
        const lastCompletedIndex = flatModules.findIndex(m => 
          m.module_id && m.module_id.toString() === lastCompletedModuleId.toString()
        );
        
        if (lastCompletedIndex !== -1 && lastCompletedIndex < flatModules.length - 1) {
          // Load the next module after the last completed one
          moduleToLoad = flatModules[lastCompletedIndex + 1];
          console.log('Loading next module after last completed:', moduleToLoad.module_id);
        } else if (lastCompletedIndex !== -1) {
          // If last completed was the final module, still load it
          moduleToLoad = flatModules[lastCompletedIndex];
          console.log('Loading last completed module (last in sequence):', moduleToLoad.module_id);
        }
      }
      
      // If no last completed module, load the first module
      if (!moduleToLoad) {
        if (modules[0]?.sub_modules && modules[0].sub_modules.length > 0) {
          moduleToLoad = modules[0].sub_modules[0];
        } else {
          moduleToLoad = modules[0];
        }
        console.log('No completed modules, loading first available module:', moduleToLoad.module_id);
      }
      
      if (moduleToLoad) {
        setSelectedModuleId(moduleToLoad.module_id);
        loadModuleContent(moduleToLoad.module_id);
      }
    }
  }, [modules, selectedModuleId, lastCompletedModuleId]);

  const fetchModules = async () => {

     const userDetails = JSON.parse(sessionStorage.getItem('userDetail') || '{}');
    try {
      setLoader(true);
      const token = apiService.getAuthToken();
      
      // Get the lastModuleId from URL query parameters if available
      const urlParams = new URLSearchParams(window.location.search);
      const lastModuleIdFromURL = urlParams.get('lastModuleId');
      
      // console.log('Fetching modules for user ID:', userDetails?.uid);
      // console.log('Course Meta ID:', courseMetaId);
      // console.log('Course Meta ID:', courseId);

      
      const res = await apiService.getLmsv1(`getMicroModules/${userDetails?.uid}/${courseMetaId}`, token);
      if (res?.status && Array.isArray(res.data)) {
        setModules(res.data);
        
        // Extract course name if available in the response
        // Check if course name is in the response data
        if (res.course_name) {
          console.log(`Setting course name from API response: ${res.course_name} for course ${courseMetaId}`);
          setCourseName(res.course_name);
          localStorage.setItem(courseNameKey, res.course_name);
        } else if (res.data && res.data.length > 0 && res.data[0].course_name) {
          console.log(`Setting course name from data array: ${res.data[0].course_name} for course ${courseMetaId}`);
          setCourseName(res.data[0].course_name);
          localStorage.setItem(courseNameKey, res.data[0].course_name);
        } else {
          // Fallback to stored course name or default
          const storedCourseName = localStorage.getItem(courseNameKey);
          if (storedCourseName) {
            console.log(`Using stored course name: ${storedCourseName} for course ${courseMetaId}`);
            setCourseName(storedCourseName);
          }
        }
        
        // Get completed modules from API response
        const apiCompletedModuleIds = res.data.filter(mod => mod.is_completed).map(mod => mod.module_id);
        
        // Find all completed modules by recursively checking nested modules (sub-modules only)
        const findAllCompletedModules = (modules) => {
          let completed = [];
          if (!modules || !Array.isArray(modules)) return completed;
          
          modules.forEach(module => {
            // Only check sub_modules, not the main module
            if (module.sub_modules && Array.isArray(module.sub_modules)) {
              module.sub_modules.forEach(subModule => {
                if (subModule.is_completed) {
                  completed.push(subModule.module_id);
                }
                
                // Also check sub_sub_modules
                if (subModule.sub_sub_modules && Array.isArray(subModule.sub_sub_modules)) {
                  subModule.sub_sub_modules.forEach(subSubModule => {
                    if (subSubModule.is_completed) {
                      completed.push(subSubModule.module_id);
                    }
                  });
                }
              });
            }
          });
          
          return completed;
        };
        
        // Get all completed modules including nested ones from API response only
        const allApiCompletedModules = findAllCompletedModules(res.data);
        
        // Set completed modules state from API response only (no localStorage merge)
        setCompletedModules(allApiCompletedModules);
        
        // Update modules state to reflect completion for main modules based on sub-modules
        setModules(prev => {
          return res.data.map(module => {
            let updatedModule = { ...module };
            
            // Check if all sub-modules are completed to mark main module as completed
            if (updatedModule.sub_modules && Array.isArray(updatedModule.sub_modules)) {
              const allSubModulesCompleted = updatedModule.sub_modules.every(subModule => {
                const isSubModuleCompleted = subModule.is_completed || allApiCompletedModules.includes(subModule.module_id);
                if (!isSubModuleCompleted) return false;
                
                // Also check sub_sub_modules
                if (subModule.sub_sub_modules && subModule.sub_sub_modules.length > 0) {
                  return subModule.sub_sub_modules.every(subSubModule => 
                    subSubModule.is_completed || allApiCompletedModules.includes(subSubModule.module_id)
                  );
                }
                return true;
              });
              
              updatedModule.is_completed = allSubModulesCompleted;
            }
            
            return updatedModule;
          });
        });
        
        console.log('Completed modules from API:', allApiCompletedModules);
        
        // Find the last completed module
        const lastCompletedModule = [...res.data].reverse().find(mod => mod.is_completed);
        if (lastCompletedModule) {
          setLastCompletedModuleId(lastCompletedModule.module_id);
          localStorage.setItem(lastCompletedModuleKey, lastCompletedModule.module_id);
        }
        
        // Determine which module to load first (prioritize in this order):
        // 1. Module ID from URL query parameter (if valid)
        // 2. Next module after the last completed module (if user has completed modules)
        // 3. First module from API response (first main module's first sub-module)
        
        let moduleToLoad = null;
        
        // Try to find the module specified in URL query parameters
        if (lastModuleIdFromURL) {
          // Find the module with this ID in the flattened modules array
          moduleToLoad = findModuleById(res.data, lastModuleIdFromURL);
        }
        
        // If no module found from URL, check if user has a last completed module
        if (!moduleToLoad && lastCompletedModuleId) {
          console.log('Last completed module ID found:', lastCompletedModuleId);
          
          // Get all modules in a flattened array for easier navigation
          const flatModules = flattenModules(res.data);
          
          // Find the index of the last completed module
          const lastCompletedIndex = flatModules.findIndex(m => 
            m.module_id && m.module_id.toString() === lastCompletedModuleId.toString()
          );
          
          // If found and it's not the last module, load the next one
          if (lastCompletedIndex !== -1 && lastCompletedIndex < flatModules.length - 1) {
            moduleToLoad = flatModules[lastCompletedIndex + 1];
            console.log('Loading next module after last completed:', moduleToLoad.module_id);
          }
          // If it's the last module, still load it (we'll load the last completed)
          else if (lastCompletedIndex !== -1) {
            moduleToLoad = flatModules[lastCompletedIndex];
            console.log('Loading last completed module (last in sequence):', moduleToLoad.module_id);
          }
        }
        
        // If still no module found, default to first module
        if (!moduleToLoad) {
          // Try to get the first module from the response
          if (res.data.length > 0) {
            // Check if there are sub_modules
            if (res.data[0]?.sub_modules && res.data[0]?.sub_modules.length > 0) {
              moduleToLoad = res.data[0].sub_modules[0];
            } else {
              // If no sub_modules, use the first module itself
              moduleToLoad = res.data[0];
            }
            console.log('Loading first available module:', moduleToLoad.module_id);
          }
        }

        if (moduleToLoad) {
          console.log('Module to load found:', moduleToLoad);
          
          // Set the module as selected by default
          setSelectedModuleId(moduleToLoad.module_id);
          
          // Load the module content
          try {
            await loadModuleContent(moduleToLoad.module_id);
            console.log('Initial module content loaded successfully');
          } catch (contentErr) {
            console.error('Error loading initial module content:', contentErr);
            CustomToast.error("Error loading module content");
          }
        } else {
          console.error('No modules found to load');
          CustomToast.error("No modules found for this course");
        }
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
      // CustomToast.error("Error loading modules");
    } finally {
      setLoader(false);
    }
  };

  const loadModuleContent = async (moduleId) => {
    try {
      if (!moduleId) {
        console.log('No module ID provided to loadModuleContent, determining appropriate module');
        
        // If modules are loaded and no specific module ID, determine which module to load
        if (modules.length > 0) {
          // First check if there's a last completed module to load the next one after it
          if (lastCompletedModuleId) {
            const flatModules = flattenModules(modules);
            const lastCompletedIndex = flatModules.findIndex(m => 
              m.module_id && m.module_id.toString() === lastCompletedModuleId.toString()
            );
            
            if (lastCompletedIndex !== -1 && lastCompletedIndex < flatModules.length - 1) {
              // Next module after last completed
              moduleId = flatModules[lastCompletedIndex + 1].module_id;
              console.log('No moduleId provided, loading next module after last completed:', moduleId);
            } else if (lastCompletedIndex !== -1) {
              // Last completed module (if it was the last in sequence)
              moduleId = lastCompletedModuleId;
              console.log('No moduleId provided, loading last completed module:', moduleId);
            }
          }
          
          // If still no moduleId determined (no completed modules), load the first module
          if (!moduleId) {
            const firstModule = modules[0]?.sub_modules && modules[0]?.sub_modules.length > 0 
              ? modules[0].sub_modules[0] 
              : modules[0];
              
            if (firstModule) {
              moduleId = firstModule.module_id;
              console.log('No moduleId provided and no completed modules, falling back to first module:', moduleId);
            } else {
              return; // No modules available
            }
          }
        } else {
          return; // No modules available
        }
      }
      
      setLoader(true);
      const token = apiService.getAuthToken();
      
      console.log('Loading content for module ID:', moduleId);
      
      console.log(`Searching for module with ID: ${moduleId} among ${modules.length} modules`);
      
      // Flatten modules for easier debugging
      const allModules = flattenModules(modules);
      console.log(`Total flattened modules: ${allModules.length}`);
      
      if (moduleId === "686bac5f60cac39e9f7cf242") {
        console.log("Found problematic module ID in loadModuleContent");
        // Log some module IDs to help debug
        console.log("Available module IDs:", allModules.map(m => m.module_id).slice(0, 5)); // Show first 5 for brevity
      }
      
      // Find the selected module to get its content type (search in nested structure)
      // Use our helper function to find the module
      let selectedModule = findModuleById(modules, moduleId);
      
      // If no modules in state yet or module not found, try to get first available module
      if (!selectedModule && modules.length === 0) {
        console.log('No modules in state yet, proceeding with API call');
      } else if (!selectedModule) {
        console.warn(`Module not found for ID: ${moduleId}. Attempting to handle this situation.`);
        
        // Log for the specific problematic ID
        if (moduleId === "686bac5f60cac39e9f7cf242") {
          console.log("This is the problematic ID we're trying to fix");
          // Try to directly search in the flattened array as an alternative approach
          const directMatch = allModules.find(m => 
            m.module_id && m.module_id.toString() === moduleId.toString()
          );

          if (directMatch) {
            console.log("Found module using direct flattened search!");
            selectedModule = directMatch;
          } else {
            console.log("Module still not found with direct search");
          }
        }
        
        // If still no module found, try fallbacks
        if (!selectedModule) {
          // Try to fetch the module content directly from API even if we couldn't find it in our local state
          // We'll still attempt the API call since the module might exist on the server
          console.log(`Attempting direct API call for module ID: ${moduleId}`);
          
          // If modules exist, try to find a suitable fallback
          if (modules.length > 0) {
            let fallbackModule = null;
            
            // First check if we have a previously completed module and try to load the next one
            if (lastCompletedModuleId) {
              const flattenedModules = flattenModules(modules);
              const lastCompletedIndex = flattenedModules.findIndex(m => 
                m.module_id && m.module_id.toString() === lastCompletedModuleId.toString()
              );
              
              if (lastCompletedIndex !== -1 && lastCompletedIndex < flattenedModules.length - 1) {
                fallbackModule = flattenedModules[lastCompletedIndex + 1];
                console.log('Falling back to next module after last completed:', fallbackModule.module_id);
              }
            }
            
            // If no fallback yet, use the first module
            if (!fallbackModule) {
              if (modules[0].sub_modules && modules[0].sub_modules.length > 0) {
                fallbackModule = modules[0].sub_modules[0];
              } else {
                fallbackModule = modules[0];
              }
              console.log('Falling back to first available module:', fallbackModule.module_id);
            }
            
            selectedModule = fallbackModule;
            moduleId = selectedModule.module_id;
            
            // Update the selected module ID in state
            setSelectedModuleId(moduleId);
            // Also update URL without causing a full page reload
            try {
              window.history.replaceState(null, '', `/dashboard/course-module/${courseMetaId}?lastModuleId=${moduleId}`);
            } catch (urlErr) {
              console.warn('Could not update URL state:', urlErr);
            }
          } else {
            CustomToast.error("No modules found for this course");
            setLoader(false);
            return;
          }
        }
      }
      
      // Even if we couldn't find the module in our state, we can still try to get its content from the API
      // But create a placeholder module to use as fallback
      if (!selectedModule) {
        console.warn(`Module with ID ${moduleId} not found in local state. Creating placeholder and trying API call directly.`);
        // Create a placeholder module with minimal information
        selectedModule = {
          module_id: moduleId,
          name: `Module ${moduleId}`, // Default name
          title: `Module ${moduleId}`, // Default title
          content_type: null, // Will be determined from API response
          module_type: null // Will be determined from API response
        };
      }
      
      console.log('Selected module to use for content:', selectedModule);
      
      // Fetch content based on the module ID
      console.log(`Fetching content for module ID: ${moduleId} from API`);
      
      // API call to get module content
      const res = await apiService.getLmsv1(`getMicroModuleContent/${moduleId}`, token);
      console.log(`API response status for module ${moduleId}:`, res?.status ? 'Success' : 'Failed');
      
      if (res?.status && res.data && res.data.length > 0) {
        const content = res.data[0]; 
        console.log('Module content received:', content);
        console.log('Available properties in content:', Object.keys(content));
        
        // Add content type from selected module if not present in content
        if (!content.type && selectedModule.content_type) {
          content.type = selectedModule.content_type;
        }
        
        // Also check for module_type as fallback
        if (!content.type && selectedModule.module_type) {
          content.type = selectedModule.module_type;
        }
        
        // Add name from selected module if not present in content
        if (!content.name && selectedModule.name) {
          content.name = selectedModule.name;
        }
        
        // Add title from selected module if not present in content
        if (!content.title && selectedModule.title) {
          content.title = selectedModule.title;
        }
        
        // Shuffle questions for quiz/test type content
        if (content.questions && ['base_line', 'end_line'].includes(content.course_test_type)) {
          content.questions = shuffleArray(content.questions);
        }
        
        setSelectedModuleId(moduleId);
        setSelectedModule(content);
      } else {
        console.error('Failed to load module content - invalid response:', res);
        CustomToast.error("Failed to load module content");
        
        // Use minimal module data when API fails
        const fallbackContent = {
          _id: Date.now().toString(), // Generate a temporary ID
          module_id: moduleId,
          name: selectedModule.name || `Module ${moduleId}`,
          title: selectedModule.title || `Module ${moduleId}`,
          type: selectedModule.content_type || selectedModule.module_type || "content",
          description: "Content could not be loaded. Please try again later.",
          course_id: courseMetaId
        };
        
        console.log('Using fallback content:', fallbackContent);
        setSelectedModuleId(moduleId);
        setSelectedModule(fallbackContent);
      }
    } catch (err) {
      console.error('Error loading module content:', err);
      CustomToast.error("Failed to load module content");
    } finally {
      setLoader(false);
    }
  };

  // Callback function to handle selected answers from Quiz component
  const handleSelectedAnswers = (answers) => {
    setSelectedAns(answers);
    console.log('Selected answers received in microModuleDashboard:', answers);
  };

  const handleModuleCompletion = async (moduleId = selectedModuleId) => {
    try {
      console.log('Completing module with ID:', moduleId);
   
      // First try to sync with server
      const synced = await syncModuleCompletionWithServer(moduleId);
      
      if (synced) {
        console.log('Module completion synced with server successfully');
        
        // Update last completed module ID
        setLastCompletedModuleId(moduleId);
        localStorage.setItem(lastCompletedModuleKey, moduleId);
        
        // Find next module to load
        const flatModules = flattenModules(modules);
        const currentIndex = flatModules.findIndex(m => m.module_id === moduleId);
        console.log('Current module index:', currentIndex);
        
        if (currentIndex !== -1 && currentIndex < flatModules.length - 1) {
          const nextModule = flatModules[currentIndex + 1];
          console.log('Next module found:', nextModule);
          
          // Load next module content
          await loadModuleContent(nextModule.module_id);
        } else {
          // All modules completed or no next module
          console.log('All modules completed or this was the last module');
        }
      } else {
        console.warn('Module completion not synced with server');
      }
    } catch (err) {
      console.error('Error completing module:', err);
      CustomToast.error("Error completing module");
    }
  };

const toggleOpen = (id) => {
  setOpenIds(prev =>
    prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
  );
};

  const handleModuleClick = async (module) => {
    try {
      // Prevent loading the same module if already selected
      if (selectedModuleId === module.module_id) {
        return;
      }
      
      // Check if user can access this module (module progression logic)
      const flatModules = flattenModules(modules);
      const moduleIndex = flatModules.findIndex(m => m.module_id === module.module_id);
      const currentModuleIndex = flatModules.findIndex(m => m.module_id === selectedModuleId);
      
      // Allow access if:
      // 1. Module is already completed
      // 2. Module is the first module
      // 3. Previous module is completed
      // 4. User is going back to a previous module
      const canAccess = module.is_completed || 
                       completedModules.includes(module.module_id) ||
                       moduleIndex === 0 ||
                       moduleIndex <= currentModuleIndex ||
                       (moduleIndex > 0 && (flatModules[moduleIndex - 1].is_completed || 
                        completedModules.includes(flatModules[moduleIndex - 1].module_id)));
      
      if (!canAccess) {
        CustomToast.error("Please complete the previous module first");
        return;
      }
      
      await loadModuleContent(module.module_id);
    } catch (err) {
      CustomToast.error("Error loading module");
    }
  };

  const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  // Function to sync module completion status with the server
  const syncModuleCompletionWithServer = async (moduleId) => {
    try {
      const token = apiService.getAuthToken();
      
      // Get the module content to have all necessary information
      const moduleContent = selectedModule;
      
      if (!moduleContent || !moduleContent._id) {
        console.error('Module content not found for syncing completion status');
        return;
      }
      
      // Prepare payload for API
      const payload = {
        org_id: orgInfo?.org_id,
        email: userData?.email,
        user_id: userData?.uid,
        micro_course_id: microCourseId,
        lang_id: JSON.parse(selectedLanguage)?.id,
        module_id: moduleId,
        module_content_id: moduleContent._id,
        selected_ans: selectedAns,
        module_status: "completed",
        course_status: moduleContent?.content_type == "last_module" || course?.course_status === "completed" ? "completed" : "inprogress",
        last_module_id: lastCompletedModuleId || moduleContent?.module_id,
      };
      
      console.log('Syncing module completion with server:', payload);
      
      // Send the completion status to server
      const result = await apiService.postLms("saveUserMicroCourseAns", payload, token);
      
      if (result && result.success) {
        // Update modules completion status based on API response
        const moduleCompletionData = result.data?.module_completion;
        
        if (moduleCompletionData && Array.isArray(moduleCompletionData)) {
          const completedModuleIds = moduleCompletionData
            .filter(item => item.module_status === "completed" && item.module_id)
            .map(item => item.module_id);
          
          console.log('Completed module IDs from API:', completedModuleIds);
          
          // Update completed modules state
          setCompletedModules(completedModuleIds);
          
          // Update modules state to reflect completion from API
          setModules(prev => {
            const updatedModules = prev.map(module => {
              const updateModuleCompletion = (mod) => {
                const isCompleted = completedModuleIds.includes(mod.module_id);
                return { ...mod, is_completed: isCompleted };
              };
              
              // Don't update main module completion directly - it will be determined by sub-modules
              let updatedModule = { ...module };
              
              // Update sub_modules
              if (updatedModule.sub_modules && Array.isArray(updatedModule.sub_modules)) {
                updatedModule.sub_modules = updatedModule.sub_modules.map(updateModuleCompletion);
                
                // Update sub_sub_modules
                updatedModule.sub_modules = updatedModule.sub_modules.map(subModule => {
                  if (subModule.sub_sub_modules && Array.isArray(subModule.sub_sub_modules)) {
                    subModule.sub_sub_modules = subModule.sub_sub_modules.map(updateModuleCompletion);
                  }
                  return subModule;
                });
                
                // Check if all sub-modules are completed to mark main module as completed
                const allSubModulesCompleted = updatedModule.sub_modules.every(subModule => {
                  const isSubModuleCompleted = subModule.is_completed || completedModuleIds.includes(subModule.module_id);
                  if (!isSubModuleCompleted) return false;
                  
                  // Also check sub_sub_modules
                  if (subModule.sub_sub_modules && subModule.sub_sub_modules.length > 0) {
                    return subModule.sub_sub_modules.every(subSubModule => 
                      subSubModule.is_completed || completedModuleIds.includes(subSubModule.module_id)
                    );
                  }
                  return true;
                });
                
                updatedModule.is_completed = allSubModulesCompleted;
              }
              
              return updatedModule;
            });
            
            return updatedModules;
          });
          
          // Check if course is completed and this is the last module
          const flatModules = flattenModules(modules);
          const currentModuleIndex = flatModules.findIndex(m => m.module_id === moduleId);
          const isLastModule = currentModuleIndex === flatModules.length - 1;
          
          // Show CourseCompletedModal only if course status is completed and this is the last module
          if (result.data?.course_status === "completed" && isLastModule && 
              moduleCompletionData.some(item => item.module_id === moduleId && item.module_status === "completed")) {
            setLastModuleCompleted(true);
            console.log('Course completed, showing completion modal');
          }
        }
        
        return true;
      } else {
        console.error('Failed to sync module completion with server');
        return false;
      }
    } catch (error) {
      console.error('Error syncing module completion:', error);
      return false;
    }
  };

  // Function to clear module completion data (useful for logout)
  const clearModuleCompletionData = () => {
    // Clear only essential localStorage items, not completion tracking
    localStorage.removeItem(lastCompletedModuleKey);
    // Also clear the course name for this specific course
    localStorage.removeItem(courseNameKey);
    setCompletedModules([]);
    setLastCompletedModuleId(null);
    setCourseName('');
  };

  // Listen for logout event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user_details' && !e.newValue) {
        // User logged out
        clearModuleCompletionData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <div className="d-flex micro-module-dashboard nosidebar-page__main bg-white lms" style={{ background: '#F0F0F0 0% 0% no-repeat padding-box', height: '100vh' }}>
      {loader && <Loader />}
      <Sidebar
        modules={modules}
        selectedModuleId={selectedModuleId}
        onModuleSelect={handleModuleClick}
        completedModules={completedModules}
        openIds={openIds}
        toggleOpen={toggleOpen}
        courseName={courseName}
        course={course}
        key={`sidebar-${completedModules.length}`} 
      />
      <div className="flex-grow-1" style={{ background: '#F0F0F0 0% 0% no-repeat padding-box', height: '100vh' }}>
       
        {selectedModule &&
          <CourseContent selectedModule={selectedModule} course={course} onComplete={handleModuleCompletion}>
            <div className='d-flex justify-content-start align-items-center border-bottom pb-4 mb-4'>
              <img src='/images/module-icon.svg' alt="Module" className='me-2' />
              <h1 className='fw-bold font-26 mb-0'>{selectedModule?.name}</h1>
            </div>

            {
           // selectedModule?.type === "video" ? <Video moduleContent={selectedModule} updateLmsModule={() => handleModuleCompletion(selectedModule.module_id)} course={course} /> : 
             
             selectedModule?.type === "content" ? <Reading moduleContent={selectedModule} updateLmsModule={() => handleModuleCompletion(selectedModule.module_id)} course={course} lastCompletedModuleId={lastCompletedModuleId} completedModules={completedModules} /> :
             selectedModule?.type === "carousal" ? <Carousel moduleContent={selectedModule} updateLmsModule={() => handleModuleCompletion(selectedModule.module_id)} course={course} lastCompletedModuleId={lastCompletedModuleId} completedModules={completedModules} /> :
             selectedModule?.type === "document" ? <Document moduleContent={selectedModule} updateLmsModule={() => handleModuleCompletion(selectedModule.module_id)} course={course} lastCompletedModuleId={lastCompletedModuleId} completedModules={completedModules} /> : <Quiz moduleContents={selectedModule} courseDetails={selectedModule} updateLmsModule={() => handleModuleCompletion(selectedModule.module_id)} course={course} selectedAns={selectedAns} onAnswersChange={handleSelectedAnswers} lastCompletedModuleId={lastCompletedModuleId} completedModules={completedModules} /> }
          </CourseContent>
        }
      </div>
    </div>
  );
};

export default MicroModuleDashboard;